import Image from 'components/Common/Image/Image'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import { useAppSelector } from 'hooks/reduxHooks'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import type { MouseEventHandler } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import TripDrawer, { type TripDrawerAnimation } from 'tripPlanner/components/GenericDrawer/TripDrawer'
import TripDrawerHeaderTitle from 'tripPlanner/components/GenericDrawer/TripDrawerHeaderTitle'
import AddToTripAction from 'tripPlanner/components/GenericDrawer/AddToTripAction'
import { createLocationDrawInformationStream } from 'tripPlanner/api'
import { useTextStream } from 'tripPlanner/hooks/api/useTextStream'
import ImageGalleryLightbox from 'LuxKit/Carousel/ImageGalleryLightbox'
import type { ImageParams } from 'components/Common/ResponsiveImage'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import LineImagesIcon from 'LuxKit/Icons/line/LineImagesIcon'
import MarkdownRender from 'LuxKit/MarkdownRender'
import type { DrawImagesCategory } from 'tripPlanner/hooks/api/locationImage'
import { useLocationDrawPlaceDetails } from 'tripPlanner/hooks/api/locationImage'
import type { LocationInfoData } from 'tripPlanner/components/Chat/Message'
import GooglePlaceRatingDisplay from 'tripPlanner/components/GooglePlaceRating/GooglePlaceRatingDisplay'
import Divider from 'LuxKit/Divider'
import type { TripItineraryData } from 'tripPlanner/api/types'
import {
  ContentWrapper,
  GridContainer,
  GalleryButtons,
  GridImageClickable,
  PRIMARY_GALLERY_IMAGE_CLASS,
  SECONDARY_GALLERY_IMAGE_CLASS,
} from './TripPlanDetailDrawer.styles'
import {
  shouldRenderTripDrawerTitleInContent,
  useRightPanelTripDrawerPresentation,
} from 'tripPlanner/components/GenericDrawer/TripDrawerPresentationContext'
import TripDrawerAssistantBridge, { TripDrawerAssistantFooter } from './TripDrawerAssistantBridge'
import { getDrawerGalleryImageKey, useDrawerGalleryImages } from '../hooks/useDrawerGalleryImages'

export type { LocationInfoData, DrawImagesCategory }

const mainImageProps: ImageParams = { tabletWidth: '800px', quality: 'best' }
const defaultImageProps: ImageParams = {
  tabletWidth: '400px',
  quality: 'good',
}
const GALLERY_PREVIEW_IMAGE_COUNT = 1

interface Props {
  isOpen: boolean
  onClose: () => void
  onAfterClose?: () => void
  onBack?: () => void
  animation?: TripDrawerAnimation
  locationData: LocationInfoData
  category?: DrawImagesCategory
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string) => void
}

function LocationInfoDrawer({
  isOpen,
  onClose,
  onAfterClose,
  onBack,
  animation,
  locationData,
  category,
  itineraryData,
  onSendMessage,
}: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const locale = useAppSelector((state) => state.geo.locale)
  const drawerPresentation = useRightPanelTripDrawerPresentation()
  const showTitleInContent = shouldRenderTripDrawerTitleInContent(drawerPresentation)

  const {
    data: drawData,
    isLoading: isImagesLoading,
    isError: isImagesError,
    refetch: refetchImages,
  } = useLocationDrawPlaceDetails(isOpen ? locationData : undefined, category)

  const imageIdentifiers = useMemo(
    () => drawData?.imageIdentifiers ?? [],
    [drawData?.imageIdentifiers],
  )
  const rating = drawData?.rating
  const userRatingCount = drawData?.userRatingCount
  const placeCategory = drawData?.category

  const cacheKey = useMemo(
    () => [
      'locationInfo',
      locationData.title,
      locationData.placeSearch,
      locale ?? '',
    ],
    [locationData.title, locationData.placeSearch, locale],
  )

  const createStream = useMemo(() => {
    if (!isOpen) return undefined
    return () => (onChunk: (chunk: string) => void, signal: AbortSignal) =>
      createLocationDrawInformationStream(
        {
          title: locationData.title,
          description: locationData.description,
          emoji: locationData.emoji,
          placeSearch: locationData.placeSearch,
        },
        undefined,
        onChunk,
        signal,
        locale,
      )
  }, [
    isOpen,
    locationData.title,
    locationData.description,
    locationData.emoji,
    locationData.placeSearch,
    locale,
  ])

  const {
    content: streamedContent,
    isLoading: isStreamLoading,
    isError: isStreamError,
    retry: handleRetryStream,
  } = useTextStream(cacheKey, createStream)

  const handleRetryImages = useCallback(() => {
    refetchImages()
  }, [refetchImages])

  const allGalleryImages = useMemo((): Array<App.Image> => {
    if (!imageIdentifiers) return []
    return imageIdentifiers.map((imageId): App.Image => {
      const isUrl = imageId.startsWith('http')
      return isUrl ? { url: imageId } : { id: imageId }
    })
  }, [imageIdentifiers])

  const {
    availableImages: availableGalleryImages,
    gridImages,
    imageCount: gridImageCount,
    handleImageError: handleGalleryImageError,
  } = useDrawerGalleryImages({
    images: allGalleryImages,
    resetKey: `${locationData.title}:${locationData.placeSearch}`,
    previewImageCount: showTitleInContent ? GALLERY_PREVIEW_IMAGE_COUNT : undefined,
  })

  const handleGalleryImageClick = useCallback<MouseEventHandler<HTMLElement>>(
    (event) => {
      const imageIndex = Number(event.currentTarget.dataset.index) || 0
      showModal(
        <ImageGalleryLightbox
          images={availableGalleryImages}
          defaultIndex={imageIndex}
        />,
      )
    },
    [showModal, availableGalleryImages],
  )

  const openGalleryLightbox = useCallback(() => {
    showModal(
      <ImageGalleryLightbox images={availableGalleryImages} defaultIndex={0} />,
    )
  }, [showModal, availableGalleryImages])

  const handleAfterClose = useCallback(() => {
    onAfterClose?.()
  }, [onAfterClose])

  const addToTripAction =
    itineraryData && onSendMessage ? (
      <AddToTripAction
        itineraryData={itineraryData}
        onSendMessage={onSendMessage}
        placeName={locationData.title}
        onAfterAction={onClose}
      />
    ) : undefined

  const failedToLoadImagesText = intl.formatMessage({
    id: 'v7oB_L7PCKMyw4hLg5mX',
    defaultMessage: 'Failed to load images.',
    description:
      'Error text when location images fail to load when an issue occurs',
  })

  const retryLabel = intl.formatMessage({
    id: 'Qd-v3N4jmhtCiKBTNhJP',
    defaultMessage: 'Retry',
    description: 'Action button label to retry a failed loading action',
  })

  const viewGalleryLabel = intl.formatMessage({
    id: 'dT0vZCOXM35rab49jQTk',
    defaultMessage: 'View gallery',
    description: 'Action button label to open the image gallery lightbox',
  })

  const failedToLoadLocationText = intl.formatMessage({
    id: 'lcIsZuHuABqMAx7IHJIu',
    defaultMessage: 'Failed to load location information.',
    description: 'Error text when location information fails to load',
  })

  return (
    <TripDrawer
      isOpen={isOpen}
      onClose={onClose}
      onAfterClose={handleAfterClose}
      onBack={onBack}
      animation={animation}
      direction="right-to-left"
      presentation={drawerPresentation}
      header={!showTitleInContent ? <TripDrawerHeaderTitle title={locationData.title} /> : undefined}
      headerActions={addToTripAction}
      footer={
        <TripDrawerAssistantFooter
          presentation={drawerPresentation}
          contextLabel={locationData.title}
          isOpen={isOpen}
        />
      }
    >
      <TripDrawerAssistantBridge
        presentation={drawerPresentation}
        isOpen={isOpen}
        contextLabel={locationData.title}
      />
      <Group direction="vertical" horizontalAlign="center">
        <ContentWrapper direction="vertical" gap={24}>
          {/* Image Gallery Grid */}
          {isImagesLoading && (
            <LoadingBox
              height={280}
              borderRadius="L"
              style={{ width: '100%' }}
            />
          )}
          {showTitleInContent && isImagesError && (
            <Heading variant="heading3">
              {locationData.title}
            </Heading>
          )}
          {isImagesError && (
            <Group
              direction="vertical"
              gap={8}
              horizontalAlign="center"
              verticalAlign="center"
            >
              <BodyText variant="medium" colour="critical">
                {failedToLoadImagesText}
              </BodyText>
              <TextButton
                kind="secondary"
                size="small"
                onClick={handleRetryImages}
              >
                {retryLabel}
              </TextButton>
            </Group>
          )}
          {!isImagesLoading && !isImagesError && gridImages.length > 0 && (
            <GridContainer
              $imageCount={gridImageCount}
              $matchOfferPageAspect
            >
              {gridImages.map(({ image, lightboxIndex }, gridImageIndex) => (
                <GridImageClickable
                  className={gridImageIndex === 0 ? PRIMARY_GALLERY_IMAGE_CLASS : SECONDARY_GALLERY_IMAGE_CLASS}
                  key={image.id ?? image.url}
                  onClick={handleGalleryImageClick}
                  data-index={lightboxIndex}
                >
                  {image.id && (
                    <ResponsiveImage
                      id={image.id}
                      fit="center"
                      data-image-key={getDrawerGalleryImageKey(image)}
                      onError={handleGalleryImageError}
                      {...(gridImageIndex === 0 ? mainImageProps : defaultImageProps)}
                    />
                  )}
                  {image.url && (
                    <Image
                      image={image}
                      fit="center"
                      data-image-key={getDrawerGalleryImageKey(image)}
                      onError={handleGalleryImageError}
                    />
                  )}
                </GridImageClickable>
              ))}
              {availableGalleryImages.length > 1 && (
                <GalleryButtons
                  direction="horizontal"
                  gap={8}
                  horizontalAlign="end"
                >
                  <TextButton
                    kind="secondary"
                    variant="dark"
                    startIcon={<LineImagesIcon />}
                    onClick={openGalleryLightbox}
                  >
                    {viewGalleryLabel}
                  </TextButton>
                </GalleryButtons>
              )}
            </GridContainer>
          )}
          {showTitleInContent && !isImagesError && (
            <Heading variant="heading3">
              {locationData.title}
            </Heading>
          )}
          {isImagesLoading && (
            <LoadingBox height={20} width={160} borderRadius="S" />
          )}
          {!isImagesLoading && !isImagesError && (rating || placeCategory) && (
            <Group direction="horizontal" gap={8} verticalAlign="center">
              {rating && (
                <GooglePlaceRatingDisplay
                  rating={rating}
                  ratingsCount={userRatingCount}
                  size="S"
                />
              )}
              {placeCategory && rating && (
                <Divider kind="primary" direction="vertical" />
              )}
              {placeCategory && (
                <BodyText variant="medium" colour="light-foreground-alt">
                  {placeCategory}
                </BodyText>
              )}
            </Group>
          )}

          {/* Streamed Markdown Content */}
          {isStreamLoading && (
            <Group direction="vertical" gap={8}>
              <LoadingBox
                height={20}
                borderRadius="S"
                style={{ width: '100%' }}
              />
              <LoadingBox
                height={20}
                borderRadius="S"
                style={{ width: '90%' }}
              />
              <LoadingBox
                height={20}
                borderRadius="S"
                style={{ width: '95%' }}
              />
              <LoadingBox
                height={20}
                borderRadius="S"
                style={{ width: '80%' }}
              />
              <LoadingBox
                height={20}
                borderRadius="S"
                style={{ width: '85%' }}
              />
            </Group>
          )}
          {streamedContent && (
            <MarkdownRender
              content={streamedContent}
              fontSize="medium"
              type="compact"
            />
          )}
          {isStreamError && (
            <Group
              direction="vertical"
              gap={8}
              horizontalAlign="center"
              verticalAlign="center"
            >
              <BodyText variant="medium" colour="critical">
                {failedToLoadLocationText}
              </BodyText>
              <TextButton
                kind="secondary"
                size="small"
                onClick={handleRetryStream}
              >
                {retryLabel}
              </TextButton>
            </Group>
          )}
        </ContentWrapper>
      </Group>
    </TripDrawer>
  )
}

export default LocationInfoDrawer
